import { useContext, useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { useResizeDetector } from "react-resize-detector";
import DashboardService from "../../../../services/DashboardService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";

export default function DocumentBarChart() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [chartWidth, setChartWidth] = useState(0);
  const { width, ref } = useResizeDetector();
  useEffect(() => {
    const t = setTimeout(() => width && setChartWidth(width), 100);
    return () => clearTimeout(t);
  }, [width]);

useEffect(() => {
    const controller = new AbortController();

    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await DashboardService.GetDocumentChart(
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (!response) return;
        if (response.isSuccess) {
          setDocuments(response.data);
        } else {
          setError("Failed to fetch documents");
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        setError("Failed to fetch documents");
        console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchDocuments();

    return () => controller.abort(); 
  }, [authContext]);

  const nameLookup = {};  
  const uniqueCourses = {};
  documents.forEach((doc) => {
    uniqueCourses[doc.courseCode] = doc.courseName;
  });

  const filteredDocs =
    selectedCourse === "All"
      ? documents
      : documents.filter((d) => d.courseCode === selectedCourse);

  const courseWiseRaw = {};

  filteredDocs.forEach((doc) => {
    const key = `${doc.courseName} (${doc.courseCode})`; 
    nameLookup[key] = doc.courseName; 

    courseWiseRaw[key] = (courseWiseRaw[key] || 0) + doc.docCount;
  });

  const categoryRaw = {};
  filteredDocs.forEach((doc) => {
    const key = `${doc.courseName} (${doc.courseCode})`;
    nameLookup[key] = doc.courseName;

    if (!categoryRaw[key]) categoryRaw[key] = {};
    const type = doc.docType || "General";
    categoryRaw[key][type] =
      (categoryRaw[key][type] || 0) + doc.docCount;
  });
  const realKeys = Object.keys(courseWiseRaw);

  const allHeader = ["Documents", ...realKeys.map((k) => nameLookup[k])];
  const allRow = ["Docs", ...realKeys.map((k) => courseWiseRaw[k])];
  const makeCategoryData = (key) => {
    const map = categoryRaw[key] || {};
    const header = ["Documents", ...Object.keys(map)];
    const row = ["Docs", ...Object.values(map)];
    return { header, row };
  };

  const selectedKey =
    selectedCourse === "All"
      ? null
      : Object.keys(courseWiseRaw).find((key) =>
          key.endsWith(`(${selectedCourse})`)
        );

  const { header, row } =
    selectedCourse === "All"
      ? { header: allHeader, row: allRow }
      : makeCategoryData(selectedKey);

  const chartData = [header, row];
  const totalDocs = row.slice(1).reduce((sum, v) => sum + v, 0);
  const barCount = header.length - 1;

  const options = {
    backgroundColor: "transparent",
    chartArea: { width: "80%", height: "65%", bottom: 85 },
    isStacked: false,
    legend: {
      position: "bottom",
      textStyle: {
        color: isDark ? "#eaeaea" : "#444",
        fontSize: 11,
        fontName: "Poppins",
      },
    },
    hAxis: {
      gridlines: { color: isDark ? "#444" : "#eaeaea" },
      textStyle: {
        color: isDark ? "#eaeaea" : "#444",
        fontSize: 10,
        fontName: "Poppins",
      },
    },
    vAxis: {
      gridlines: { color: isDark ? "#444" : "#eaeaea" },
      textStyle: {
        color: isDark ? "#eaeaea" : "#444",
        fontSize: 10,
        fontName: "Poppins",
      },
    },
    tooltip: {
      isHtml: false,
      textStyle: { fontSize: 10, fontName: "Poppins" },
      showColorCode: true,
    },
    bar: {
      groupWidth:
        barCount > 12
          ? "45%"
          : barCount > 8
          ? "65%"
          : barCount > 4
          ? "55%"
          : "25%",
    },
    colors: header
      .slice(1)
      .map((_, i) => ["#0F3D8C", "#4BB6FF", "#1e88e5", "#7DC9FF"][i % 4]),
  };

  useEffect(() => {
    const iv = setInterval(() => {
      document
        .querySelectorAll("svg rect[fill]")
        .forEach((bar) => bar.setAttribute("rx", "6"));
    }, 300);
    return () => clearInterval(iv);
  }, [selectedCourse, chartWidth]);

  if (error)
    return <div style={{ padding: "14px", color: "red" }}>{error}</div>;

  return (
    <div
      ref={ref}
      className="charts"
      style={{
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        width: "100%",
        height: "357px",
        display: "flex",
        border: "1px solid #eee",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {loading ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton width="120px" height="20px" />
            <Skeleton width="100px" height="24px" />
          </div>
          <Skeleton width="140px" height="15px" className="mt-1" />
          <Skeleton
            width="100%"
            height="280px"
            borderRadius="8px"
            className="mt-3 mb-2"
          />
        </>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 16 }}>Documents</div>
              <p
                style={{
                  fontSize: 13,
                  color: isDark ? "#eaeaea" : "#6c757d",
                }}
              >
                Total Documents: <b>{totalDocs}</b>
              </p>
            </div>

            <div
               title={selectedCourse === "All" ? "" : uniqueCourses[selectedCourse]}
              style={{ display: "inline-block", maxWidth: "100px" }}
            >
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                style={{
                  fontSize: 12,
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  padding: "4px 8px",
                  backgroundColor: isDark ? "#222" : "#fff",
                  color: isDark ? "#eaeaea" : "#000",
                  maxWidth: "100px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                }}
              >
                <option value="All">All</option>
                {Object.entries(uniqueCourses).map(([code, name]) => (
                  <option key={code} value={code} >
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "10px" }}>
            <Chart
              chartType="ColumnChart"
              width={chartWidth ? `${chartWidth - 25}px` : "100%"}
              height="265px"
              data={chartData}
              options={options}
            />
          </div>
        </div>
      )}
    </div>
  );
}